import React from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { ContentTitle } from './atoms';

type Content = Parameters<typeof ContentTitle>[0]['content'];

export interface ContentLinkProps {
  content: Content;
  onClick?: (content: Content) => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Minimal title-as-hyperlink presentation. Renders the content's title via
 * `ContentTitle` (single-line, ellipsis on overflow) wrapped in a clickable
 * box that behaves like a text link: hover underline + color shift toward
 * the brand cyan accent.
 *
 * When `disabled` is true, the link is muted and non-interactive (used for
 * drafts and other non-viewable items).
 *
 * No auth, no router. Caller wires navigation via `onClick`.
 */
export const ContentLink: React.FC<ContentLinkProps> = ({
  content,
  onClick,
  disabled,
  sx,
}) => {
  const interactive = !disabled && typeof onClick === 'function';
  const handleClick = interactive ? () => onClick!(content) : undefined;

  return (
    <Box
      onClick={handleClick}
      sx={[
        (theme) => ({
          display: 'block',
          minWidth: 0,
          cursor: interactive ? 'pointer' : 'default',
          color: disabled
            ? theme.palette.text.disabled ?? theme.palette.text.secondary
            : theme.palette.text.primary,
          opacity: disabled ? 0.6 : 1,
          textDecoration: 'none',
          transition: 'color 0.15s ease',
          ...(interactive && {
            '&:hover': {
              color: theme.palette.brand?.cyan ?? theme.palette.primary.main,
              '& > *': {
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                color: 'inherit',
              },
            },
          }),
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <ContentTitle content={content} clamp={1} />
    </Box>
  );
};

export default ContentLink;
